/**
 * SIX VIEWS, NOT FIFTEEN CARDS. The room's built-in tables each answered a
 * slice of one question: Search market, Demand, Who ranks and Who goes after
 * whom were four cards about the same searches. A view is one question, and
 * its tables are tabs inside it. Nothing is lost: every table is one click
 * away, and the cards and links that name a table still open it.
 *
 * Pure, so jest can pin it.
 */
import type { BoardroomDeliverable } from '../../../types';

export interface ViewTab {
  kind: string;
  label: string;
}

export interface RoomView {
  key: string;
  label: string;
  /** In order: the first one on the dashboard is the card's face. */
  tabs: ViewTab[];
}

export const VIEWS: RoomView[] = [
  {
    key: 'competitors',
    label: 'Competitors',
    tabs: [
      { kind: 'competitors', label: 'List' },
      { kind: 'landscape', label: 'Map' },
    ],
  },
  { key: 'pricing', label: 'Pricing', tabs: [{ kind: 'price_map', label: 'Plans' }] },
  {
    key: 'features',
    label: 'Features',
    tabs: [
      { kind: 'feature_cloud', label: 'Cloud' },
      { kind: 'feature_matrix', label: 'Matrix' },
    ],
  },
  {
    key: 'search',
    label: 'Search',
    tabs: [
      { kind: 'search_market', label: 'Terms' },
      { kind: 'demand', label: 'Demand' },
      { kind: 'who_ranks', label: 'Who shows up' },
      { kind: 'brand_grid', label: 'Who goes after whom' },
    ],
  },
  {
    key: 'selling',
    label: 'How they sell',
    tabs: [
      { kind: 'appeal', label: 'Angles' },
      { kind: 'email_capture', label: 'Lead magnets' },
      { kind: 'contacts', label: 'Contacts' },
    ],
  },
  { key: 'complaints', label: 'Complaints', tabs: [{ kind: 'pain_gap', label: 'List' }] },
  { key: 'audience', label: 'Where they talk', tabs: [{ kind: 'audience', label: 'Places' }] },
  { key: 'ideas', label: 'Ideas', tabs: [{ kind: 'opportunity_brief', label: 'Ideas' }] },
];

/**
 * A STORY ROOM'S VIEWS: what a reporter gathers, not what a buyer compares.
 * The same search and complaint tables, named for what they answer here, next
 * to the story's own (`boardroom/room_kinds.py` says which kinds are stories).
 */
export const STORY_VIEWS: RoomView[] = [
  { key: 'story', label: 'Story so far', tabs: [{ kind: 'story', label: 'Timeline' }] },
  { key: 'people', label: 'Who', tabs: [{ kind: 'people', label: 'People' }] },
  { key: 'says', label: 'What people say', tabs: [{ kind: 'pain_gap', label: 'List' }] },
  {
    key: 'search',
    label: 'Search interest',
    tabs: [
      { kind: 'search_market', label: 'Terms' },
      { kind: 'demand', label: 'Demand' },
    ],
  },
  // ITS OWN VIEW, not a Search tab: as a tab its row sat under "Search
  // interest" on the dashboard while the Tables list showed it by name.
  { key: 'coverage', label: 'Who covers it', tabs: [{ kind: 'who_ranks', label: 'Who covers it' }] },
  { key: 'angles', label: 'Angles to write', tabs: [{ kind: 'angles', label: 'Angles' }] },
  ...VIEWS.filter(v => !['search', 'complaints'].includes(v.key)),
];

const STORY_KINDS = new Set(['person', 'topic', 'interest']);

/** Whether a room of this kind is a story rather than a market. */
export const isStoryRoom = (roomKind?: string | null) => STORY_KINDS.has(roomKind ?? '');

/** The views for this kind of room. */
export function viewsFor(roomKind?: string | null): RoomView[] {
  return isStoryRoom(roomKind) ? STORY_VIEWS : VIEWS;
}

/** The view a card belongs to: a room's own table, not a filtered copy or one
 *  the chat built (those have a subject and stay cards of their own). */
export function viewOf(d: Pick<BoardroomDeliverable, 'kind' | 'subject'>, roomKind?: string | null): RoomView | null {
  if (d.subject) return null;
  return viewsFor(roomKind).find(v => v.tabs.some(t => t.kind === d.kind)) ?? null;
}

export interface DashboardTile {
  /** The card whose numbers the tile shows: the view's first table on the list. */
  lead: BoardroomDeliverable;
  /** Every card of the view on the list, the lead first. Just the lead when it is no view's. */
  members: BoardroomDeliverable[];
  view: RoomView | null;
}

/** The dashboard's tiles: one per view with a table on the list, where the
 *  view's first table is; the rest one each. Order is the list's. Pure. */
export function dashboardTiles(listed: BoardroomDeliverable[], roomKind?: string | null): DashboardTile[] {
  const out: DashboardTile[] = [];
  const byView = new Map<string, DashboardTile>();
  for (const d of listed) {
    const view = viewOf(d, roomKind);
    if (!view) {
      out.push({ lead: d, members: [d], view: null });
      continue;
    }
    const tile = byView.get(view.key);
    if (tile) {
      tile.members.push(d);
      continue;
    }
    const fresh: DashboardTile = { lead: d, members: [d], view };
    byView.set(view.key, fresh);
    out.push(fresh);
  }
  // The face is the view's FIRST TAB that is on the list, not whichever was
  // pinned first: Search shows its terms, not who goes after whom.
  for (const tile of out) {
    if (!tile.view) continue;
    const order = (k: string) => tile.view!.tabs.findIndex(t => t.kind === k);
    tile.members.sort((a, b) => order(a.kind) - order(b.kind));
    tile.lead = tile.members[0];
  }
  return out;
}

/** A table's name as a place: "Search · Demand", or "Pricing" for a view of
 *  one table. Null for a card that is no view's. Pure. */
export function viewTitle(kind: string, subject = '', roomKind?: string | null): string | null {
  const view = viewOf({ kind, subject }, roomKind);
  if (!view) return null;
  if (view.tabs.length < 2) return view.label;
  const tab = view.tabs.find(t => t.kind === kind);
  return tab ? `${view.label} · ${tab.label}` : view.label;
}
